/**
 * Font definitions for the theme system.
 * Fonts are loaded dynamically from Google Fonts.
 */

export type Font = 'inter' | 'noto-sans' | 'nunito-sans' | 'figtree'

export interface FontDefinition {
  id: Font
  name: string
  family: string
  googleFont: string
  preview: string
  weights: number[]
}

export const FONTS: FontDefinition[] = [
  {
    id: 'inter',
    name: 'Inter',
    family: '"Inter", ui-sans-serif, system-ui, sans-serif',
    googleFont: 'Inter:wght@400;500;600;700',
    preview: 'Designers love packing quirky glyphs into test phrases.',
    weights: [400, 500, 600, 700],
  },
  {
    id: 'noto-sans',
    name: 'Noto Sans',
    family: '"Noto Sans", ui-sans-serif, system-ui, sans-serif',
    googleFont: 'Noto+Sans:wght@400;500;600;700',
    preview: 'Designers love packing quirky glyphs into test phrases.',
    weights: [400, 500, 600, 700],
  },
  {
    id: 'nunito-sans',
    name: 'Nunito Sans',
    family: '"Nunito Sans", ui-sans-serif, system-ui, sans-serif',
    googleFont: 'Nunito+Sans:wght@400;500;600;700',
    preview: 'Designers love packing quirky glyphs into test phrases.',
    weights: [400, 500, 600, 700],
  },
  {
    id: 'figtree',
    name: 'Figtree',
    family: '"Figtree", ui-sans-serif, system-ui, sans-serif',
    googleFont: 'Figtree:wght@400;500;600;700',
    preview: 'Designers love packing quirky glyphs into test phrases.',
    weights: [400, 500, 600, 700],
  },
]

export function getFontById(id: Font): FontDefinition | undefined {
  return FONTS.find((f) => f.id === id)
}

// Track loaded fonts to avoid duplicate loading
const loadedFonts = new Set<string>()

/**
 * Dynamically load a font from Google Fonts.
 * Safe to call multiple times - will only load once.
 */
export function loadFont(fontId: Font): void {
  if (loadedFonts.has(fontId)) return

  const font = getFontById(fontId)
  if (!font) return

  // Check if link already exists
  const existingLink = document.querySelector(`link[data-font="${fontId}"]`)
  if (existingLink) {
    loadedFonts.add(fontId)
    return
  }

  // Create and append link element
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${font.googleFont}&display=swap`
  link.setAttribute('data-font', fontId)
  document.head.appendChild(link)

  loadedFonts.add(fontId)
}

/**
 * Preload all fonts for the font selector preview.
 */
export function preloadAllFonts(): void {
  FONTS.forEach((font) => loadFont(font.id))
}

/**
 * Apply a font to the document.
 */
export function applyFont(fontId: Font): void {
  const font = getFontById(fontId)
  if (!font) return

  // Ensure font is loaded
  loadFont(fontId)

  // Apply to root
  document.documentElement.style.setProperty('--font-sans', font.family)
}
